<template>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 gap-y-8">
        <div
            @click.stop.prevent="goToLink(variant.buyLink)"
            v-for="variant in variants"
            :key="variant.id"
            class="cursor-pointer relative p-6 rounded-lg flex flex-col justify-between transform transition hover:scale-102"
            :style="{
                'background-color': pricing.backColor,
                'box-shadow': `0 0 0 0px #fff, 0 0 0 ${variant.highlight ? '2px' : '1px'} ${
                    variant.highlight ? pricing.highlightColor : pricing.borderColor
                }, 0 0 #0000`,
                'border-radius': `${pricing.borderRadius}px`,
            }"
        >
            <div class="">
                <div
                    v-if="variant.badge"
                    class="absolute inset-x-0 top-0 translate-y-px transform z-10"
                >
                    <div class="flex -translate-y-1/2 transform justify-center">
                        <cUtilsEdit @edit="goToVariantField(variant.id, 'variantBadge')">
                            <span
                                :style="{
                                    'background-color': variant.highlight
                                        ? pricing.highlightColor
                                        : pricing.backColor,
                                    color: variant.highlight
                                        ? pricing.backColor
                                        : pricing.headingsColor,
                                    'border-color': variant.highlight
                                        ? pricing.highlightColor
                                        : pricing.borderColor,
                                }"
                                class="border inline-flex rounded-full px-4 py-1 text-base font-semibold"
                            >
                                {{ variant.badge }}
                            </span>
                        </cUtilsEdit>
                    </div>
                </div>
                <div
                    v-if="variant.name"
                    :style="{
                        'background-color': variant.highlight
                            ? hexToRgba(pricing.highlightColor, '0.1')
                            : pricing.backColor,
                        color: variant.highlight ? pricing.highlightColor : pricing.headingsColor,
                    }"
                    class="inline-block mb-5 py-1 px-2 rounded-md text-sm"
                >
                    <cUtilsEdit @edit="goToVariantField(variant.id, 'variantName')">
                        <span>{{ variant.name }}</span>
                    </cUtilsEdit>
                </div>
                <div v-if="variant.img" class="mb-3">
                    <cUtilsEdit @edit="goToVariantField(variant.id, 'variantImg')">
                        <img class="rounded-lg w-16 h-16 object-cover" :src="variant.img" />
                    </cUtilsEdit>
                </div>
                <div class="pb-6">
                    <div
                        v-if="variant.title"
                        class="text-lg leading-tight font-semibold mb-2"
                        :style="{ color: pricing.headingsColor }"
                    >
                        <cUtilsEdit @edit="goToVariantField(variant.id, 'variantTitle')">
                            <MarkdownHtml :markdown="variant.title" />
                        </cUtilsEdit>
                    </div>
                    <div
                        class="text-3xl leading-none font-semibold mb-4"
                        :style="{ color: pricing.headingsColor }"
                    >
                        {{ variant.promoPrice ? variant.promoPrice : variant.price }}
                        <span
                            v-if="variant.promoPrice && variant.promoPrice != variant.price"
                            class="text-base font-normal line-through opacity-80"
                            :style="{ color: pricing.headingsColor }"
                        >
                            {{ variant.price }}
                        </span>
                        <span
                            :style="{ color: pricing.headingsColor }"
                            class="font-medium text-lg opacity-80"
                        >
                            {{ variant.priceInfo }}
                        </span>
                    </div>

                    <div
                        :style="{ color: pricing.headingsColor }"
                        class="text-sm mb-4 leading-normal opacity-80"
                    >
                        <cUtilsEdit @edit="goToVariantField(variant.id, 'variantDesc')">
                            <MarkdownHtml linksBlank :markdown="variant.desc" />
                        </cUtilsEdit>
                    </div>
                    <cUtilsEdit
                        v-if="variant.features.length"
                        @edit="goToLayoutField('pricingFeatLabel')"
                    >
                        <div
                            class="text-sm font-medium mt-8 mb-2"
                            :style="{ color: pricing.headingsColor }"
                        >
                            {{ pricing.featLabel }}
                        </div>
                    </cUtilsEdit>
                    <cUtilsEdit
                        v-if="variant.features.length"
                        @edit="goToVariantField(variant.id, 'variantPoints')"
                    >
                        <ul role="list" class="text-gray-600 text-sm">
                            <li
                                v-for="feature in variant.features"
                                :key="variant.name + feature.name"
                                class="flex items-center mb-2"
                                :style="{ color: pricing.headingsColor }"
                            >
                                <CheckIcon
                                    v-if="feature.positive"
                                    class="flex-shrink-0 w-5 h-5 text-green-500 mr-1"
                                />
                                <XIcon
                                    v-if="feature.negative"
                                    class="flex-shrink-0 w-5 h-5 text-red-500 mr-1"
                                />
                                <div class="opacity-80">{{ feature.name }}</div>
                            </li>
                        </ul>
                    </cUtilsEdit>
                </div>
            </div>
            <div>
                <div class="relative">
                    <a
                        :href="variant.buyLink"
                        type="button"
                        :style="
                            variant.highlight
                                ? {
                                      'background-color': pricing.highlightColor,
                                      color: pricing.backColor,
                                      'box-shadow': `0 0 0 0px #fff, 0 0 0 1px ${pricing.highlightColor}, 0 0 #0000`,
                                      'border-radius': `${pricing.borderRadius}px`,
                                  }
                                : {
                                      'background-color': 'transparent',
                                      color: pricing.headingsColor,
                                      'box-shadow': `0 0 0 0px #fff, 0 0 0 1px ${pricing.borderColor}, 0 0 #0000`,
                                      'border-radius': `${pricing.borderRadius}px`,
                                  }
                        "
                        :class="[variant.highlight ? '' : 'pricing-btn']"
                        class="relative block w-full px-3 py-2 text-center rounded-md border border-transparent ring-1 ring-gray-200 text-base font-medium focus:z-10 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 opacity-90 hover:opacity-100"
                    >
                        <cUtilsEdit
                            v-if="variant.btnLabel"
                            @edit="goToVariantField(variant.id, 'variantBtnLabel')"
                        >
                            <span> {{ variant.btnLabel }}</span>
                        </cUtilsEdit>
                        <cUtilsEdit v-else @edit="goToLayoutField('pricingBtnLabel')">
                            <span> {{ pricing.btnLabel }}</span>
                        </cUtilsEdit>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { CheckIcon, XIcon } from "@easy/ui/icons";
import { MarkdownHtml } from "@easy/ui/text";
import { hexToRgba } from "@/shared/utils/helpers";
import { usePricingGoTo } from "@/views/tools/pricing/utils/usePricingGoTo";
import { useGoToLink } from "@/shared/utils/router/useGoToLink";
import cUtilsEdit from "@/views/shared/cUtilsEdit.vue";

export default {
    name: "GridBottomLayout",
    components: {
        CheckIcon,
        XIcon,
        cUtilsEdit,
        MarkdownHtml,
    },
    props: {
        pricing: Object,
        variants: Array,
    },
    setup() {
        const { goToLink } = useGoToLink("_self");
        const { goToVariantField, goToLayoutField } = usePricingGoTo();
        return {
            goToVariantField,
            goToLayoutField,
            goToLink,
        };
    },
    methods: {
        hexToRgba,
    },
};
</script>
